import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Building2, FileText, ReceiptText, Send, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { StatusChip } from "../AdminJobCards";
import { ReadOnlyBanner, HighValueBadge, DetailHeader, Section, DL, inr, inrDec, fmtDate, fmtDateTime, safe, arr } from "./shared";

const TASK_STATUS_MAP = {
  pending: "Pending",
  in_progress: "In Progress",
  paused: "Paused",
  completed: "Completed",
  cancelled: "Cancelled",
};

const PART_STATUS_MAP = {
  required: "Required",
  reserved: "Reserved",
  ordered: "Ordered",
  received: "Received",
  issued: "Issued",
  enquiry_submitted: "Enquiry Submitted",
  quoted: "Quoted",
  ordered_from_sh: "Ordered (SH)",
  cancelled: "Cancelled",
  returned: "Returned",
  unavailable: "Unavailable",
};

export default function AdminJobCardDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/ops/job-cards/${id}`);
        if (live) setData(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [id]);

  if (loading) return <div className="p-10 text-center text-[#667085]">Loading job card…</div>;
  if (!data) return <div className="p-10 text-center text-[#e63946]">Job card not found.</div>;

  const jc = data.job_card;
  const garage = data.garage || {};
  const customer = data.customer || {};
  const cs = jc.customer_snapshot || {};
  const vs = jc.vehicle_snapshot || {};
  const linked = data.linked || {};
  const veh = data.vehicle_master || {};

  const tasks = arr(jc.tasks);
  const parts = arr(jc.parts_needed);
  const findings = jc.findings || {};
  const advance = jc.advance || {};
  const damage = arr(jc.damage_markers);
  const activity = arr(jc.activity);
  const futureWork = arr(jc.future_work);
  const complaints = arr(jc.complaints);
  const photos = arr(jc.photos);

  return (
    <div className="space-y-4 max-w-6xl" data-testid="admin-jc-detail">
      <DetailHeader
        crumb="Back to Job Cards"
        backTo="/admin/job-cards"
        title={jc.number || jc.job_number || `JC ${(jc.id || "").slice(0, 8)}`}
        subtitle={`${garage.name || "—"} · Created ${fmtDate(jc.created_at)}`}
        right={
          <>
            <StatusChip status={jc.status} />
            <HighValueBadge show={data.high_value} />
            <button
              onClick={() => window.print()}
              data-testid="jc-print-btn"
              className="h-8 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5"
            >
              <Printer size={13} /> Print
            </button>
          </>
        }
      />

      <ReadOnlyBanner />

      {/* Top summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Value</div>
          <div className="text-[20px] font-bold text-[#1d3557] font-mono mt-0.5">{inr(data.computed_value)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Expected Delivery</div>
          <div className="text-[14px] font-semibold text-[#1d3557] mt-0.5">{fmtDate(jc.promised_at)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Priority</div>
          <div className="text-[14px] font-semibold text-[#1d3557] mt-0.5 capitalize">{safe(jc.priority)}</div>
        </div>
        <div className="bg-white border border-[#E4E7EC] rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Advance Received</div>
          <div className="text-[14px] font-semibold text-[#1d3557] mt-0.5">{advance.received ? inr(advance.amount) : "No"}</div>
        </div>
      </div>

      {/* Links */}
      <div className="flex flex-wrap gap-2">
        {garage.id && (
          <Link to={`/admin/garages/${garage.id}`} data-testid="jc-open-garage" className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:border-[#457b9d] inline-flex items-center gap-1.5">
            <Building2 size={13} /> Open Garage
          </Link>
        )}
        {linked.estimate && (
          <Link to={`/admin/estimates/${linked.estimate.id}`} data-testid="jc-open-estimate" className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#457b9d] hover:border-[#457b9d] inline-flex items-center gap-1.5">
            <FileText size={13} /> Estimate {linked.estimate.number}
          </Link>
        )}
        {linked.invoice && (
          <Link to={`/admin/invoices/${linked.invoice.id}`} data-testid="jc-open-invoice" className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#457b9d] hover:border-[#457b9d] inline-flex items-center gap-1.5">
            <ReceiptText size={13} /> Invoice {linked.invoice.number}
          </Link>
        )}
        {linked.enquiry && (
          <Link to={`/admin/enquiries/${linked.enquiry.id}`} data-testid="jc-open-enquiry" className="h-8 px-3 rounded-md bg-white border border-[#E4E7EC] text-[12px] font-semibold text-[#457b9d] hover:border-[#457b9d] inline-flex items-center gap-1.5">
            <Send size={13} /> Enquiry {linked.enquiry.number}
          </Link>
        )}
      </div>

      {/* Customer */}
      <Section title="Customer Details" testId="jc-customer-section">
        <DL cols={3} pairs={[
          ["Name", customer.name || cs.name],
          ["Mobile", customer.phone || cs.phone],
          ["WhatsApp", customer.whatsapp || cs.whatsapp],
          ["Email", customer.email || cs.email],
          ["Address", customer.address || cs.address],
          ["City", customer.city || cs.city],
          ["State", customer.state || cs.state],
        ]} />
      </Section>

      {/* Vehicle */}
      <Section title="Vehicle Details" testId="jc-vehicle-section">
        <DL cols={3} pairs={[
          ["Registration", vs.reg_number || veh.reg_number],
          ["Make", vs.make || veh.make],
          ["Model", vs.model || veh.model],
          ["Variant", vs.variant || veh.variant],
          ["Fuel Type", vs.fuel_type || veh.fuel_type],
          ["VIN / Chassis", vs.vin || veh.vin],
          ["Engine No.", veh.engine_number || vs.engine_number],
          ["Odometer", jc.odometer != null ? `${jc.odometer} km` : "—"],
          ["Fuel Level", jc.fuel_level],
        ]} />
      </Section>

      {/* Job */}
      <Section title="Job Details" testId="jc-job-section">
        <DL cols={3} pairs={[
          ["Service Type", jc.service_type],
          ["Repair Type", jc.repair_type],
          ["Service Advisor", jc.service_advisor_name || jc.service_advisor_id],
          ["Expected Delivery", fmtDateTime(jc.promised_at)],
          ["Created By", jc.created_by_name || jc.created_by],
          ["Last Updated", fmtDateTime(jc.updated_at)],
        ]} />
        {(complaints || []).length > 0 && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-2">Customer Complaints</div>
            <ul className="space-y-1.5">
              {complaints.map((c, i) => (
                <li key={c?.id || i} className="text-[13px] text-[#1d3557] pl-3 border-l-2 border-[#457b9d]/40">{safe(typeof c === "string" ? c : (c?.text || c?.complaint || c))}</li>
              ))}
            </ul>
          </div>
        )}
        {(findings.observation || findings.probable_cause || findings.recommended) && (
          <div className="mt-5">
            <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-2">Service Advisor Findings</div>
            <DL cols={2} pairs={[
              ["Observation", findings.observation],
              ["Probable Cause", findings.probable_cause],
              ["Recommended", findings.recommended],
              ["Immediate Attention", findings.immediate_attention ? "Yes" : "No"],
              ["Road Test", findings.road_test_required ? "Yes" : "No"],
            ]} />
          </div>
        )}
      </Section>

      {/* Tasks */}
      {tasks.length > 0 && (
        <Section title={`Tasks (${tasks.length})`} testId="jc-tasks-section">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[12px]">
              <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Assigned</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Labour</th>
                  <th className="px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1FAEE]">
                {tasks.map((t, i) => (
                  <tr key={t?.id || i}>
                    <td className="px-3 py-2 font-semibold text-[#1d3557]">{safe(typeof t === "string" ? t : (t?.name || t?.title))}</td>
                    <td className="px-3 py-2">{safe(t?.assigned_to_name || t?.assigned_to)}</td>
                    <td className="px-3 py-2">{TASK_STATUS_MAP[t?.status] || safe(t?.status)}</td>
                    <td className="px-3 py-2 text-right font-mono">{inr(t?.labour_amount || t?.amount)}</td>
                    <td className="px-3 py-2 text-[#667085]">{safe(t?.notes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Parts */}
      {parts.length > 0 && (
        <Section title={`Parts Required (${parts.length})`} testId="jc-parts-section">
          <div className="overflow-x-auto">
            <table className="min-w-full text-[12px]">
              <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-3 py-2">Part</th>
                  <th className="px-3 py-2">Part #</th>
                  <th className="px-3 py-2 text-right">Qty</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Unit Price</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1FAEE]">
                {parts.map((p, i) => {
                  const qty = Number(p?.qty || p?.quantity || 0);
                  const price = Number(p?.selling_price || p?.price || 0);
                  return (
                    <tr key={p?.id || i}>
                      <td className="px-3 py-2 font-semibold text-[#1d3557]">{safe(p?.name || p?.part_name)}</td>
                      <td className="px-3 py-2 font-mono text-[#667085]">{safe(p?.part_number)}</td>
                      <td className="px-3 py-2 text-right font-mono">{qty}</td>
                      <td className="px-3 py-2">{PART_STATUS_MAP[p?.status] || safe(p?.status)}</td>
                      <td className="px-3 py-2 text-right font-mono">{inr(price)}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{inr(price * qty)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Inspection */}
      {(damage.length > 0 || photos.length > 0) && (
        <Section title="Vehicle Inspection" testId="jc-inspection-section">
          {damage.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold mb-2">Damage Markers ({damage.length})</div>
              <ul className="space-y-1.5">
                {damage.map((d, i) => (
                  <li key={d?.id || i} className="text-[12px] text-[#1d3557]">
                    <span className="font-semibold capitalize">{safe(d?.side)}</span> — {safe(d?.type)} ({safe(d?.severity)}) {d?.note ? `· ${safe(d.note)}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {photos.length > 0 && (
            <div className="text-[12px] text-[#667085]">{photos.length} photo(s) attached — photo previews require the workshop app.</div>
          )}
        </Section>
      )}

      {/* Financial */}
      <Section title="Financial Information" testId="jc-financial-section">
        <DL cols={3} pairs={[
          ["Estimate Number", linked.estimate?.number],
          ["Estimate Value", linked.estimate ? inr(linked.estimate.total) : null],
          ["Estimate Status", linked.estimate?.status],
          ["Advance Received", advance.received ? `${inr(advance.amount)} · ${advance.mode || ""}` : "No"],
          ["Invoice Number", linked.invoice?.number],
          ["Invoice Total", linked.invoice ? inr(linked.invoice.total) : null],
          ["Payment Status", linked.invoice?.payment_status],
          ["Computed Value", inrDec(data.computed_value)],
        ]} />
      </Section>

      {/* Future work */}
      {futureWork.length > 0 && (
        <Section title="Future Suggested Work" testId="jc-future-section">
          <ul className="space-y-1.5">
            {futureWork.map((f, i) => (
              <li key={f?.id || i} className="text-[13px] text-[#1d3557]">• {safe(typeof f === "string" ? f : (f?.text || f?.task || f))}</li>
            ))}
          </ul>
        </Section>
      )}

      {/* Additional */}
      {(jc.internal_notes || jc.terms || jc.next_appointment) && (
        <Section title="Additional Information" testId="jc-additional-section">
          <DL cols={2} pairs={[
            ["Internal Notes", jc.internal_notes],
            ["Terms", jc.terms],
            ["Next Appointment", jc.next_appointment ? fmtDateTime(jc.next_appointment.scheduled_at) : null],
          ]} />
        </Section>
      )}

      {/* Activity */}
      {activity.length > 0 && (
        <Section title={`Activity Timeline (${activity.length})`} testId="jc-activity-section">
          <ul className="space-y-2 max-h-72 overflow-y-auto">
            {activity.slice().reverse().map((a, i) => (
              <li key={a?.id || i} className="text-[12px] text-[#1d3557] flex gap-3">
                <span className="text-[#667085] font-mono whitespace-nowrap">{fmtDateTime(a?.at)}</span>
                <span className="text-[#667085]">{safe(a?.by_name)}</span>
                <span className="flex-1">{safe(a?.note || a?.text || a?.action)}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
